'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PregnancyCalculator() {
  const t = useTranslations("tools.pregnancyCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [calcMethod, setCalcMethod] = useState('last-period');
  const [inputs, setInputs] = useState({
    dueDate: '',
    lastPeriod: '',
    cycleLength: 28,
    conceptionDate: '',
    ultrasoundDate: '',
    ultrasoundWeeks: 0,
    ultrasoundDays: 0,
    ivfDate: '',
    embryoAge: 5
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ec4899',
    primaryHover: '#be185d',
    secondary: '#f43f5e',
    accent: '#fda4af',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(236, 72, 153, 0.2)',
    background: 'linear-gradient(135deg, #fef3f2 0%, #ffe4e6 100%)',
  };

  // Calculation functions
  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const calculateDueDate = () => {
    let dueDate: Date;

    switch(calcMethod) {
      case 'due-date':
        if (!inputs.dueDate) return null;
        dueDate = new Date(inputs.dueDate);
        break;

      case 'last-period':
        if (!inputs.lastPeriod) return null;
        const lmp = new Date(inputs.lastPeriod);
        const cycleLength = inputs.cycleLength;
        const ovulationDay = cycleLength - 14;
        dueDate = addDays(lmp, 280 + (ovulationDay - 14));
        break;

      case 'conception':
        if (!inputs.conceptionDate) return null;
        const conceptionDate = new Date(inputs.conceptionDate);
        dueDate = addDays(conceptionDate, 266);
        break;

      case 'ultrasound':
        if (!inputs.ultrasoundDate) return null;
        const ultrasoundDate = new Date(inputs.ultrasoundDate);
        const totalDays = (inputs.ultrasoundWeeks * 7) + inputs.ultrasoundDays;
        dueDate = addDays(ultrasoundDate, 280 - totalDays);
        break;

      case 'ivf':
        if (!inputs.ivfDate) return null;
        const ivfDate = new Date(inputs.ivfDate);
        const embryoAge = inputs.embryoAge;
        dueDate = addDays(ivfDate, 280 - (14 + embryoAge));
        break;

      default:
        return null;
    }

    return dueDate;
  };

  const calculateWeeksPregnant = (dueDate: Date, today: Date) => {
    const daysDiff = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const weeksLeft = Math.floor(daysDiff / 7);
    const currentWeek = 40 - weeksLeft;
    const currentDay = (280 - daysDiff) % 7;
    return { week: currentWeek, day: currentDay, daysLeft: daysDiff };
  };

  const getTrimester = (week: number) => {
    if (week <= 13) return t('firstTrimester');
    if (week <= 27) return t('secondTrimester');
    return t('thirdTrimester');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const dueDate = calculateDueDate();

    if (!dueDate || isNaN(dueDate.getTime())) {
      setError(t('errorInvalidDate'));
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const pregnancy = calculateWeeksPregnant(dueDate, today);

    if (pregnancy.week < 0 || pregnancy.week > 42) {
      setError(t('errorOutOfRange'));
      return;
    }

    const lmp = addDays(dueDate, -280);
    const trimester1End = addDays(lmp, 13 * 7);
    const trimester2End = addDays(lmp, 27 * 7);

    const milestones = [
      { week: 8, desc: t('milestone8') },
      { week: 12, desc: t('milestone12') },
      { week: 20, desc: t('milestone20') },
      { week: 24, desc: t('milestone24') },
      { week: 28, desc: t('milestone28') },
      { week: 36, desc: t('milestone36') },
      { week: 37, desc: t('milestone37') },
      { week: 40, desc: t('milestone40') }
    ].map(m => ({
      ...m,
      date: addDays(lmp, m.week * 7)
    }));

    setResults({
      dueDate,
      currentWeek: pregnancy.week,
      currentDay: pregnancy.day,
      daysLeft: pregnancy.daysLeft,
      trimester: getTrimester(pregnancy.week),
      lmp,
      trimester1: { start: lmp, end: trimester1End },
      trimester2: { start: addDays(trimester1End, 1), end: trimester2End },
      trimester3: { start: addDays(trimester2End, 1), end: dueDate },
      milestones
    });
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: colors.background,
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' as const },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const
  };

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const
  };

  const selectStyle = { ...inputStyle };

  const inputRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      color: 'white',
      background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3f2 0%, #fff1f2 100%)',
      borderTop: '3px solid #ec4899'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(4, 1fr)' }
  );

  const resultCardStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    borderLeft: '4px solid #ec4899'
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#6b7280',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultValueStyle = {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.primaryHover
  };

  const timelineStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginTop: '1.5rem'
  };

  const timelineItemStyle = {
    padding: '1rem',
    marginBottom: '0.75rem',
    background: 'linear-gradient(135deg, #fef3f2 0%, #fff1f2 100%)',
    borderRadius: '0.5rem',
    borderLeft: '3px solid #ec4899'
  };

  const timelineDateStyle = {
    fontWeight: 600,
    color: colors.primaryHover,
    marginBottom: '0.25rem'
  };

  const timelineDescStyle = {
    color: '#6b7280',
    fontSize: '0.9rem'
  };

  const trimesterSectionStyle = {
    marginTop: '1.5rem',
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem'
  };

  const trimesterGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const trimesterCardStyle = {
    padding: '1rem',
    background: 'linear-gradient(135deg, #fef3f2 0%, #fff1f2 100%)',
    borderRadius: '0.5rem',
    textAlign: 'center' as const
  };

  const trimesterNumberStyle = {
    fontSize: '2rem',
    fontWeight: 700,
    color: colors.primary,
    marginBottom: '0.5rem'
  };

  const trimesterWeeksStyle = {
    color: '#6b7280',
    fontSize: '0.9rem'
  };

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(236, 72, 153, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6,
    color: colors.textSecondary
  };

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.primaryHover, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 700 }}>🤰 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="calc-method">{t('calculateBasedOn')}</label>
              <select
                id="calc-method"
                style={selectStyle}
                value={calcMethod}
                onChange={(e) => setCalcMethod(e.target.value)}
                required
              >
                <option value="last-period">{t('methodLastPeriod')}</option>
                <option value="due-date">{t('methodDueDate')}</option>
                <option value="conception">{t('methodConception')}</option>
                <option value="ultrasound">{t('methodUltrasound')}</option>
                <option value="ivf">{t('methodIVF')}</option>
              </select>
            </div>

            {calcMethod === 'due-date' && (
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="due-date">{t('yourDueDate')}</label>
                <input
                  type="date"
                  id="due-date"
                  style={inputStyle}
                  value={inputs.dueDate}
                  onChange={(e) => setInputs({...inputs, dueDate: e.target.value})}
                  required
                />
              </div>
            )}

            {calcMethod === 'last-period' && (
              <>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="last-period">{t('firstDayLastPeriod')}</label>
                  <input
                    type="date"
                    id="last-period"
                    style={inputStyle}
                    value={inputs.lastPeriod}
                    onChange={(e) => setInputs({...inputs, lastPeriod: e.target.value})}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cycle-length">{t('averageCycleLength')}</label>
                  <select
                    id="cycle-length"
                    style={selectStyle}
                    value={inputs.cycleLength}
                    onChange={(e) => setInputs({...inputs, cycleLength: parseInt(e.target.value)})}
                  >
                    {[...Array(14)].map((_, i) => {
                      const days = 22 + i;
                      return <option key={days} value={days}>{days} {t('days')}</option>;
                    })}
                  </select>
                </div>
              </>
            )}

            {calcMethod === 'conception' && (
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="conception-date">{t('conceptionDate')}</label>
                <input
                  type="date"
                  id="conception-date"
                  style={inputStyle}
                  value={inputs.conceptionDate}
                  onChange={(e) => setInputs({...inputs, conceptionDate: e.target.value})}
                  required
                />
              </div>
            )}

            {calcMethod === 'ultrasound' && (
              <>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="ultrasound-date">{t('ultrasoundDate')}</label>
                  <input
                    type="date"
                    id="ultrasound-date"
                    style={inputStyle}
                    value={inputs.ultrasoundDate}
                    onChange={(e) => setInputs({...inputs, ultrasoundDate: e.target.value})}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('pregnancyLengthAtTime')}</label>
                  <div style={inputRowStyle}>
                    <input
                      type="number"
                      min="0"
                      max="42"
                      placeholder={t('weeks')}
                      style={inputStyle}
                      value={inputs.ultrasoundWeeks}
                      onChange={(e) => setInputs({...inputs, ultrasoundWeeks: parseInt(e.target.value) || 0})}
                    />
                    <input
                      type="number"
                      min="0"
                      max="6"
                      placeholder={t('days')}
                      style={inputStyle}
                      value={inputs.ultrasoundDays}
                      onChange={(e) => setInputs({...inputs, ultrasoundDays: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </>
            )}

            {calcMethod === 'ivf' && (
              <>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="ivf-date">{t('transferDate')}</label>
                  <input
                    type="date"
                    id="ivf-date"
                    style={inputStyle}
                    value={inputs.ivfDate}
                    onChange={(e) => setInputs({...inputs, ivfDate: e.target.value})}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('embryoAge')}</label>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' as const }}>
                    {[3, 5, 6].map(day => (
                      <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' as const }}>
                        <input
                          type="radio"
                          name="embryo-age"
                          value={day}
                          checked={inputs.embryoAge === day}
                          onChange={(e) => setInputs({...inputs, embryoAge: parseInt(e.target.value)})}
                        />
                        {t('day')} {day}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            <button type="submit" style={calculateButtonStyle}>
              {t('calculateButton')}
            </button>

            {error && (
              <div style={errorMessageStyle}>{error}</div>
            )}
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ margin: '0 0 1.5rem 0', color: colors.primaryHover, fontSize: '1.5rem', textAlign: 'center' as const }}>
                {t('yourPregnancySchedule')}
              </h3>

              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('dueDate')}</span>
                  <span style={resultValueStyle}>{formatDate(results.dueDate)}</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('currentWeek')}</span>
                  <span style={resultValueStyle}>{results.currentWeek}w {results.currentDay}d</span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('daysUntilDue')}</span>
                  <span style={resultValueStyle}>
                    {results.daysLeft > 0 ? results.daysLeft : t('babyIsHere')}
                  </span>
                </div>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('currentTrimester')}</span>
                  <span style={resultValueStyle}>{results.trimester}</span>
                </div>
              </div>

              <div style={trimesterSectionStyle}>
                <h4 style={{ margin: '0 0 1rem 0', color: colors.primaryHover, fontSize: '1.2rem' }}>
                  {t('trimesterBreakdown')}
                </h4>
                <div style={trimesterGridStyle}>
                  <div style={trimesterCardStyle}>
                    <div style={trimesterNumberStyle}>1st</div>
                    <div style={trimesterWeeksStyle}>
                      {formatDate(results.trimester1.start)} - {formatDate(results.trimester1.end)}
                    </div>
                  </div>
                  <div style={trimesterCardStyle}>
                    <div style={trimesterNumberStyle}>2nd</div>
                    <div style={trimesterWeeksStyle}>
                      {formatDate(results.trimester2.start)} - {formatDate(results.trimester2.end)}
                    </div>
                  </div>
                  <div style={trimesterCardStyle}>
                    <div style={trimesterNumberStyle}>3rd</div>
                    <div style={trimesterWeeksStyle}>
                      {formatDate(results.trimester3.start)} - {formatDate(results.trimester3.end)}
                    </div>
                  </div>
                </div>
              </div>

              <div style={timelineStyle}>
                <h4 style={{ margin: '0 0 1rem 0', color: colors.primaryHover, fontSize: '1.2rem' }}>
                  {t('importantMilestones')}
                </h4>
                {results.milestones.map((milestone: any, index: number) => (
                  <div key={index} style={{...timelineItemStyle, marginBottom: index === results.milestones.length - 1 ? 0 : '0.75rem'}}>
                    <div style={timelineDateStyle}>
                      {t('week')} {milestone.week} - {formatDate(milestone.date)}
                    </div>
                    <div style={timelineDescStyle}>{milestone.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Key Features */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('keyFeaturesTitle')}</h2>

        <h3 style={h3Style}>{t('feature1Title')}</h3>
        <p style={paragraphStyle}>{t('feature1Desc')}</p>

        <h3 style={h3Style}>{t('feature2Title')}</h3>
        <p style={paragraphStyle}>{t('feature2Desc')}</p>

        <h3 style={h3Style}>{t('feature3Title')}</h3>
        <p style={paragraphStyle}>{t('feature3Desc')}</p>

        <h3 style={h3Style}>{t('feature4Title')}</h3>
        <p style={paragraphStyle}>{t('feature4Desc')}</p>

        <h3 style={h3Style}>{t('feature5Title')}</h3>
        <p style={paragraphStyle}>{t('feature5Desc')}</p>
      </section>

      {/* Clinical Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insight1Title')}</h3>
        <p style={paragraphStyle}>{t('insight1Desc')}</p>

        <h3 style={h3Style}>{t('insight2Title')}</h3>
        <p style={paragraphStyle}>{t('insight2Desc')}</p>

        <h3 style={h3Style}>{t('insight3Title')}</h3>
        <p style={paragraphStyle}>{t('insight3Desc')}</p>

        <h3 style={h3Style}>{t('insight4Title')}</h3>
        <p style={paragraphStyle}>{t('insight4Desc')}</p>
      </section>

      {/* Understanding Results */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('understandingTitle')}</h2>

        <h3 style={h3Style}>{t('understanding1Title')}</h3>
        <p style={paragraphStyle}>{t('understanding1Desc')}</p>

        <h3 style={h3Style}>{t('understanding2Title')}</h3>
        <p style={paragraphStyle}>{t('understanding2Desc')}</p>

        <h3 style={h3Style}>{t('understanding3Title')}</h3>
        <p style={paragraphStyle}>{t('understanding3Desc')}</p>

        <h3 style={h3Style}>{t('understanding4Title')}</h3>
        <p style={paragraphStyle}>{t('understanding4Desc')}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      {/* Important Considerations */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('considerationsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('consideration1')}</li>
          <li style={listItemStyle}>{t('consideration2')}</li>
          <li style={listItemStyle}>{t('consideration3')}</li>
          <li style={listItemStyle}>{t('consideration4')}</li>
          <li style={listItemStyle}>{t('consideration5')}</li>
        </ul>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={paragraphStyle}>{t('referencesIntro')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('reference1')}</li>
          <li style={listItemStyle}>{t('reference2')}</li>
          <li style={listItemStyle}>{t('reference3')}</li>
          <li style={listItemStyle}>{t('reference4')}</li>
          <li style={listItemStyle}>{t('reference5')}</li>
        </ul>
      </section>
    </div>
  );
}